// File: components/GlobalTopBar.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  SearchIcon,
  UserPlus2Icon,
  LogOut,
  Settings,
  KeyRound,
  HelpCircle,
} from "lucide-react";
import NewPatientModal from "@/components/Patient/NewPatientModal";
import SearchOverlay, { OPEN_SEARCH_EVENT } from "@/components/SearchOverlay";
import HelpPanel, { OPEN_HELP_EVENT } from "@/components/HelpPanel";
import { useTipPrefs } from "@/hooks/useTipPrefs";
import { toast } from "@/hooks/use-toast";
import { hasUnseenRelease, markReleaseSeen } from "@/lib/whatsNew";

const SEARCH_TIP_ID = "ctx-quick-search";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import { getClinic, getSettings } from "@/lib/api";

export default function GlobalTopBar() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { can } = usePermission();
  const canAddPatient = can('patients:write');
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  // Starting query when another screen opens search (e.g. Home's "Blank prescription" → pick the patient)
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  useEffect(() => {
    const open = (e: Event) => { setSearchQuery((e as CustomEvent<string>).detail); setShowSearch(true); };
    window.addEventListener(OPEN_SEARCH_EVENT, open);
    return () => window.removeEventListener(OPEN_SEARCH_EVENT, open);
  }, []);
  const [showHelp, setShowHelp] = useState(false);
  // After an app update: a quiet dot on "?" until the user opens help once (no pop-ups)
  const [hasWhatsNew, setHasWhatsNew] = useState(false);
  const [whatsNewFirst, setWhatsNewFirst] = useState(false);
  useEffect(() => { setHasWhatsNew(hasUnseenRelease(currentUser?.id)); }, [currentUser?.id]);

  const openHelp = useCallback(() => {
    const unseen = hasUnseenRelease(currentUser?.id);
    setWhatsNewFirst(unseen);
    if (unseen) { markReleaseSeen(currentUser?.id); setHasWhatsNew(false); }
    setShowHelp(true);
  }, [currentUser?.id]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Which clinic this is — the first thing on the bar, as in the design
  const [clinic, setClinic] = useState<{ name: string; sub: string | null } | null>(null);
  useEffect(() => {
    if (!currentUser?.clinic_id) return;
    let cancelled = false;
    getClinic(currentUser.clinic_id)
      .then(c => { if (!cancelled && c?.name) setClinic({ name: c.name, sub: c.city || null }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.clinic_id]);

  const [specialty, setSpecialty] = useState<string | null>(null);
  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    let cancelled = false;
    getSettings(currentUser.doctor_id, currentUser.clinic_id)
      .then(st => { if (!cancelled) setSpecialty(st?.profile?.specialty || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tips = useTipPrefs();
  const mouseSearches = useRef(0);

  // Opening search by clicking (not the shortcut) twice in a session → mention Ctrl+K once
  const openSearchByClick = () => {
    setShowSearch(true);
    mouseSearches.current += 1;
    if (mouseSearches.current === 2 && tips.isActive(SEARCH_TIP_ID)) {
      tips.markSeen(SEARCH_TIP_ID);
      const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";
      toast({ title: "Tip: search from anywhere", description: `Press ${mod}+K on any screen to find a patient or appointment.`, duration: 6000 });
    }
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowSearch(false);
        setShowDropdown(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "n" && canAddPatient) {
        e.preventDefault();
        setShowAddPatient(true);
      }
      // "?" opens help — but not while the user is typing a question mark into a field
      const t = e.target as HTMLElement;
      if (e.key === "?" && !(t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) {
        e.preventDefault();
        openHelp();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canAddPatient, openHelp]);

  // Other screens (e.g. the Home tips card) can open the help panel
  useEffect(() => {
    window.addEventListener(OPEN_HELP_EVENT, openHelp);
    return () => window.removeEventListener(OPEN_HELP_EVENT, openHelp);
  }, [openHelp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout?.();
    setShowDropdown(false);
    navigate("/login");
  };

  const shortcutLabel = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K";

  // Show "Dr." title for anyone with a clinical profile (doctor_id)
  const isDoctor = !!currentUser?.doctor_id;

  const formatUserName = (name: string | undefined) => {
    if (!name) return isDoctor ? "Doctor" : "User";
    let formattedName = name;
    if (formattedName.toLowerCase().startsWith('dr. ')) {
      formattedName = formattedName.substring(4);
    } else if (formattedName.toLowerCase().startsWith('doctor ')) {
      formattedName = formattedName.substring(7);
    }
    return formattedName;
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "D";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      {/* 56px including the border, so its bottom line meets the rail's brand row */}
      <header className="w-full h-14 bg-card border-b border-border">
        <div className="flex items-center gap-4 h-full px-4 sm:px-6">

          {/* Brand — phones only; from sm up the icon rail carries the logo */}
          <div className="flex sm:hidden items-center gap-2 min-w-0">
            <BrandMark className="w-8 h-8" />
          </div>

          {/* Clinic */}
          {clinic && (
            <div className="hidden xl:flex flex-col justify-center w-[220px] flex-shrink-0 min-w-0 leading-tight">
              <span className="text-sm font-semibold text-foreground truncate" title={clinic.name}>{clinic.name}</span>
              {clinic.sub && <span className="text-xs text-muted-foreground truncate">{clinic.sub}</span>}
            </div>
          )}

          {/* Command bar — opens the command palette (Ctrl/⌘+K) */}
          <button
            type="button"
            onClick={openSearchByClick}
            className="hidden sm:flex flex-[3] min-w-0 max-w-[560px] items-center gap-2.5 h-9 px-3 text-sm bg-input border border-border hover:border-muted-foreground/40 rounded-md transition-colors text-muted-foreground"
          >
            <SearchIcon className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 text-left truncate">Search patients, or type a command — “new Rx for Om”</span>
            <kbd className="hidden md:inline-flex h-5 select-none items-center rounded border bg-card border-border px-1.5 font-mono text-2xs font-medium text-muted-foreground">
              {shortcutLabel}
            </kbd>
          </button>

          <div className="flex-1 min-w-4" />

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Mobile search */}
            <Button
              variant="ghost"
              size="sm"
              className="sm:hidden p-2 h-9 w-9"
              onClick={openSearchByClick}
            >
              <SearchIcon className="w-4 h-4" />
            </Button>

            {canAddPatient && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowAddPatient(true)}
                className="h-9 px-3 sm:px-3.5 text-sm font-medium whitespace-nowrap"
                aria-label="New patient"
              >
                <UserPlus2Icon className="w-4 h-4 sm:hidden" />
                <span className="hidden sm:inline">New patient</span>
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="relative p-0 h-9 w-9 text-muted-foreground hover:text-foreground"
              onClick={openHelp}
              aria-label="Help and tips"
              title={hasWhatsNew ? "Help & tips — see what's new (?)" : "Help & tips (?)"}
            >
              <HelpCircle className="w-4 h-4" />
              {hasWhatsNew && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-background" />}
            </Button>

            <ThemeToggle />

            {/* Profile dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="flex items-center gap-2.5 h-10 pl-1 pr-2 rounded-md hover:bg-muted transition-colors"
                onClick={() => setShowDropdown(!showDropdown)}
                aria-haspopup="menu"
                aria-expanded={showDropdown}
                aria-label="Account menu"
              >
                <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-foreground flex-shrink-0">
                  {getInitials(formatUserName(currentUser?.name))}
                </span>
                <span className="hidden md:flex flex-col text-left leading-tight">
                  <span className="text-[13px] font-semibold text-foreground whitespace-nowrap">
                    {isDoctor ? `Dr. ${formatUserName(currentUser?.name)}` : formatUserName(currentUser?.name)}
                  </span>
                  <span className="text-xs text-muted-foreground capitalize whitespace-nowrap">
                    {specialty || currentUser?.job_role || currentUser?.role || (isDoctor ? "Doctor" : "User")}
                  </span>
                </span>
              </button>

              {showDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  {/* Dropdown header — plain, no gradient */}
                  <div className="p-3 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold text-sm">
                        {getInitials(formatUserName(currentUser?.name))}
                      </div>
                      <div>
                        <div className="font-medium text-sm text-popover-foreground">
                          {isDoctor ? `Dr. ${formatUserName(currentUser?.name)}` : formatUserName(currentUser?.name)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {currentUser?.email || ""}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="py-1">
                    {(isDoctor || currentUser?.role === 'admin') && (
                      <>
                        <button
                          className="flex items-center w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors"
                          onClick={() => { navigate("/settings"); setShowDropdown(false); }}
                        >
                          <Settings className="w-4 h-4 mr-3 text-muted-foreground" />
                          <div>
                            <div className="font-medium">Settings</div>
                            <div className="text-xs text-muted-foreground">Preferences and configuration</div>
                          </div>
                        </button>
                        <div className="my-1 border-t border-border" />
                      </>
                    )}
                    <button
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors"
                      onClick={() => { navigate("/change-password"); setShowDropdown(false); }}
                    >
                      <KeyRound className="w-4 h-4 mr-3 text-muted-foreground" />
                      <div>
                        <div className="font-medium">Change Password</div>
                        <div className="text-xs text-muted-foreground">Update your account password</div>
                      </div>
                    </button>
                    <div className="my-1 border-t border-border" />
                    <button
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      <div>
                        <div className="font-medium">Log out</div>
                        <div className="text-xs text-muted-foreground">Sign out of your account</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <NewPatientModal
        isOpen={showAddPatient}
        onClose={() => setShowAddPatient(false)}
        onPatientCreated={() => {
          setShowAddPatient(false);
          window.dispatchEvent(new CustomEvent('patient:created'));
        }}
      />
      <SearchOverlay open={showSearch} initialQuery={searchQuery} onClose={() => { setShowSearch(false); setSearchQuery(undefined); }} />
      <HelpPanel open={showHelp} onOpenChange={setShowHelp} whatsNewFirst={whatsNewFirst} />
    </>
  );
}
